/**
 * "/admin" — THE STANDING SHORTS CREW (F-142): who writes, who shoots, who
 * edits a week of Shorts.
 *
 * A Shorts week hands work to the same people every week, and every create
 * and stage move on a Shorts board opens an assignee picker — so the crew is
 * set once here and those pickers open already filled (docs/11 §4.4). The
 * picker still opens: a prefilled list is a confirmation, a skipped dialog
 * is a surprise, and a week with a stand-in editor is one uncheck away.
 *
 * Three roles, one roster: a tab per role over a single RosterPicker, rather
 * than three pickers fetching the same roster three times. Saving is explicit
 * and saves all three roles at once, like the shooting owners — this is a
 * workspace setting, and the next Shorts create anywhere reads it.
 */
import {
  SHORTS_CREW_ROLES,
  emptyShortsCrew,
  type ShortsCrew,
  type ShortsCrewRole,
  type TeamMember,
} from "@kola/shared";
import { useEffect, useState } from "react";

import { fetchShortsCrew, saveShortsCrew } from "../../core/api.js";
import { assigneesFrom, RosterPicker } from "../../ui/RosterPicker.js";
import { showToast } from "../../ui/toast.js";

const ROLE_LABEL: Record<ShortsCrewRole, string> = {
  writers: "Writers",
  shooters: "Shooters",
  editors: "Editors",
};

const ROLE_HINT: Record<ShortsCrewRole, string> = {
  writers: "Own Scripting (and the storyboard stages, if a week uses them).",
  shooters: "Own the shoot day.",
  editors: "Own the edit and the five releases.",
};

type Picked = Record<ShortsCrewRole, ReadonlySet<string>>;

function emptyPicked(): Picked {
  return { writers: new Set(), shooters: new Set(), editors: new Set() };
}

export function ShortsCrewSection() {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [picked, setPicked] = useState<Picked>(emptyPicked);
  const [role, setRole] = useState<ShortsCrewRole>("writers");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [savedBy, setSavedBy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchShortsCrew()
      .then((crew) => {
        if (cancelled) return;
        setPicked({
          writers: new Set(crew.writers.map((a) => a.uid)),
          shooters: new Set(crew.shooters.map((a) => a.uid)),
          editors: new Set(crew.editors.map((a) => a.uid)),
        });
        setSavedAt(crew.updatedAt);
        setSavedBy(crew.updatedBy);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Could not load the crew.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (uid: string) => {
    setPicked((prev) => {
      const next = new Set(prev[role]);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return { ...prev, [role]: next };
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const crew: ShortsCrew = emptyShortsCrew();
      for (const r of SHORTS_CREW_ROLES) crew[r] = assigneesFrom(team, picked[r]);
      const saved = await saveShortsCrew(crew);
      setSavedAt(saved.updatedAt);
      setSavedBy(saved.updatedBy);
      showToast("Shorts crew saved — the next Shorts week's pickers open with them");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the crew.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="admin-section">
      <div className="admin-section-head">
        <h2>Shorts crew</h2>
        <span className="admin-card-hint">
          Who a week of Shorts is handed to. Creating a Shorts week and every stage move on one
          opens its assignee picker already filled from here — change it there for the week that
          differs.
        </span>
      </div>

      <div className="kind-filter shorts-crew-tabs" role="tablist" aria-label="Crew role">
        {SHORTS_CREW_ROLES.map((r) => (
          <button
            key={r}
            type="button"
            role="tab"
            aria-selected={role === r}
            className={`kind-filter-btn${role === r ? " active" : ""}`}
            onClick={() => setRole(r)}
          >
            {ROLE_LABEL[r]}
            {picked[r].size > 0 ? ` (${picked[r].size})` : ""}
          </button>
        ))}
      </div>
      <p className="admin-card-hint shorts-crew-role-hint">{ROLE_HINT[role]}</p>

      {loading ? (
        <div className="empty-state">Loading…</div>
      ) : (
        <RosterPicker selected={picked[role]} onToggle={toggle} onLoaded={setTeam} disabled={saving} />
      )}

      {error !== null && <div className="error-note">{error}</div>}

      <div className="shooting-owners-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void save()}
          disabled={loading || saving}
        >
          {saving ? "Saving…" : "Save Shorts crew"}
        </button>
        {savedAt !== null && (
          <span className="admin-card-hint">
            Last saved {new Date(savedAt).toLocaleDateString()}
            {savedBy !== null && savedBy !== "" ? ` by ${savedBy}` : ""}
          </span>
        )}
      </div>
    </section>
  );
}
